import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeartPulse, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmDialog, EmptyState, PageHeader, Pager, Panel, RowsSkeleton, SearchBox } from "@/components/app/kit";
import { FormDialog } from "@/components/app/FormDialog";
import { useServerList } from "@/hooks/useServerList";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/i18n";

const KEYS = ["first_name", "last_name", "birth_date", "sex", "phone", "email", "address", "city", "insurance", "insurance_number", "notes"];
const FIELDS = [
  { key: "last_name", label: "Nom", required: true },
  { key: "first_name", label: "Prénom", required: true },
  { key: "birth_date", label: "Date de naissance", type: "date", validate: (v) => v && v > new Date().toISOString().slice(0, 10) && "Date future invalide" },
  { key: "sex", label: "Sexe", type: "select", options: [["F", "Féminin"], ["M", "Masculin"], ["X", "Autre / non précisé"]] },
  { key: "phone", label: "Téléphone" },
  { key: "email", label: "E-mail", type: "email", validate: (v) => v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) && "E-mail invalide" },
  { key: "address", label: "Adresse" },
  { key: "city", label: "Ville" },
  { key: "insurance", label: "Organisme d'assurance" },
  { key: "insurance_number", label: "N° d'assuré" },
  { key: "cp_name", label: "Personne à contacter" },
  { key: "cp_phone", label: "Téléphone du contact" },
  { key: "cp_relation", label: "Lien" },
  { key: "notes", label: "Notes administratives", type: "textarea", rows: 3 },
];

export default function Patients() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const list = useServerList("/t/patients", { sex: "all" });
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const save = async (v) => {
    const body = Object.fromEntries(KEYS.map((k) => [k, v[k] ?? ""]));
    body.birth_date = body.birth_date || null;
    body.email = body.email || null;
    body.contact_person = { name: v.cp_name || "", phone: v.cp_phone || "", relation: v.cp_relation || "" };
    if (edit.id) body.version = edit.version;
    if (!edit.id) {
      const { data: dups } = await api.get("/t/patients/duplicates", { params: { first_name: v.first_name, last_name: v.last_name, birth_date: v.birth_date || "", phone: v.phone || "" } });
      if (dups.length && !window.confirm(`Doublon possible : ${dups.map((d) => `${d.last_name} ${d.first_name} (${d.file_number})`).join(", ")}. Créer quand même ?`)) return;
    }
    if (edit.id) await api.put(`/t/patients/${edit.id}`, body);
    else await api.post("/t/patients", body);
    toast.success(edit.id ? "Patient mis à jour" : "Patient enregistré");
    list.reload();
  };
  const remove = async () => {
    try {
      await api.delete(`/t/patients/${del.id}`);
      toast.success("Patient supprimé");
      setDel(null);
      list.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  return (
    <div>
      <PageHeader eyebrow="Module Patients" title="Patients" subtitle="Dossiers administratifs, strictement réservés à votre cabinet."
        actions={can("patients:write") && <Button onClick={() => setEdit({ sex: "" })} data-testid="patients-create-button"><Plus className="h-4 w-4 mr-2" />Nouveau patient</Button>} />
      <Panel className="overflow-hidden">
        <div className="flex flex-col sm:flex-row gap-3 -mt-1 mb-4">
          <SearchBox value={list.filters.search} onChange={(v) => list.setFilter("search", v)} placeholder="Nom, téléphone, n° de dossier…" testid="patients-search" />
          <Select value={list.filters.sex} onValueChange={(v) => list.setFilter("sex", v)}>
            <SelectTrigger className="sm:w-44 bg-white" data-testid="patients-sex-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tous</SelectItem><SelectItem value="F">Féminin</SelectItem><SelectItem value="M">Masculin</SelectItem><SelectItem value="X">Autre</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="-mx-5 overflow-x-auto">
          {list.loading ? <RowsSkeleton /> : list.items.length === 0 ? (
            <EmptyState icon={HeartPulse} title="Aucun patient" text={list.filters.search ? "Aucun résultat pour cette recherche." : "Enregistrez votre premier patient."}
              action={can("patients:write") && !list.filters.search && <Button variant="outline" onClick={() => setEdit({ sex: "" })}>Nouveau patient</Button>} />
          ) : (
            <Table>
              <TableHeader><TableRow><TableHead>Dossier</TableHead><TableHead>Patient</TableHead><TableHead>Naissance</TableHead><TableHead>Téléphone</TableHead><TableHead>Ville</TableHead><TableHead /></TableRow></TableHeader>
              <TableBody>
                {list.items.map((p) => (
                  <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/cabinet/patients/${p.id}`)} data-testid={`patient-row-${p.file_number}`}>
                    <TableCell className="font-mono text-xs text-slate-500">{p.file_number}</TableCell>
                    <TableCell className="font-medium">{p.last_name.toUpperCase()} {p.first_name}</TableCell>
                    <TableCell className="text-sm text-slate-500">{fmtDate(p.birth_date)}</TableCell>
                    <TableCell className="text-sm">{p.phone || "—"}</TableCell>
                    <TableCell className="text-sm">{p.city || "—"}</TableCell>
                    <TableCell className="text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {can("patients:write") && <Button variant="ghost" size="sm" onClick={() => setEdit({ ...p, cp_name: p.contact_person?.name, cp_phone: p.contact_person?.phone, cp_relation: p.contact_person?.relation })} data-testid={`patient-edit-${p.file_number}`}><Pencil className="h-4 w-4" /></Button>}
                      {can("patients:delete") && <Button variant="ghost" size="sm" onClick={() => setDel(p)} data-testid={`patient-delete-${p.file_number}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <div className="-mx-5 -mb-5"><Pager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} /></div>
      </Panel>
      <FormDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} wide testid="patient-dialog" title={edit?.id ? "Modifier le patient" : "Nouveau patient"}
        initial={edit || {}} fields={FIELDS} onSubmit={save} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} destructive onConfirm={remove} confirmLabel="Supprimer"
        title="Supprimer ce patient ?" description={`Le dossier ${del?.file_number} (${del?.last_name} ${del?.first_name}) sera définitivement supprimé.`} />
    </div>
  );
}
